import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useNetworkDns } from '../api/queries';
import type { NetworkDns } from '../api/types';
import { ErrorMessage, Loading } from '../components/ui';
import { useCardTable } from '../lib/cardTable';
import { strings } from '../strings';
import { ProviderTile } from './lists/parts';
import { providerLabel } from './shared';
import type { NetworkTabProps } from './tabProps';

const d = strings.networks.lists.dns;

type Zone = NetworkDns['zones'][number];

/** One zone: its name, kind, the spokes linked to it (expandable), its conflicts and its records. */
function ZoneSection({ zone }: { zone: Zone }) {
  const [open, setOpen] = useState(false);
  const ids = useId();
  const cardTable = useCardTable();
  return (
    <section className="net-zone" aria-labelledby={`${ids}-name`} data-testid="dns-zone">
      <div className="net-zone-head">
        <h3 id={`${ids}-name`} className="font-mono text-[13px] font-semibold">
          {zone.name}
        </h3>
        <span className="badge tone-neutral">{zone.kind === 'internal' ? d.internal : d.privateLink}</span>
        {zone.links.length > 0 ? (
          <button
            type="button"
            className="net-zone-links"
            aria-expanded={open}
            aria-controls={`${ids}-links`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ChevronDown aria-hidden className="size-3.5" />
            ) : (
              <ChevronRight aria-hidden className="size-3.5" />
            )}
            {d.linked(zone.links.length)}
          </button>
        ) : (
          <span className="text-[12.5px] text-fg-muted">{d.linked(0)}</span>
        )}
      </div>
      {open && (
        <ul id={`${ids}-links`} className="net-zone-linklist">
          {zone.links.map((link) => (
            <li key={`${link.deploymentId}/${link.region}`}>
              <Link className="link" to={`/deployments/${link.deploymentId}`}>
                {link.deploymentName}
              </Link>{' '}
              <span className="text-fg-muted">· {link.region}</span>
            </li>
          ))}
        </ul>
      )}
      {zone.conflicts.map((conflict) => (
        <p
          key={`${conflict.name}/${conflict.other.deploymentId}`}
          className="callout callout-warning mt-2"
          data-testid="dns-conflict"
        >
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {d.conflict(conflict.name, conflict.owner.deploymentName, conflict.other.deploymentName)}{' '}
            <code className="font-mono text-xs">DNS_RECORD_CONFLICT</code>
          </span>
        </p>
      ))}
      {zone.records.length === 0 ? (
        <p className="help mt-2">{d.noRecords}</p>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-lg border border-line bg-surface">
          <table ref={cardTable} className="table table-cards" aria-label={d.records(zone.name)}>
            <thead>
              <tr>
                <th scope="col">{d.columns.name}</th>
                <th scope="col">{d.columns.type}</th>
                <th scope="col">{d.columns.value}</th>
                <th scope="col">{d.columns.deployment}</th>
                <th scope="col">{d.columns.resource}</th>
              </tr>
            </thead>
            <tbody>
              {zone.records.map((record) => (
                <tr key={`${record.name}/${record.type}/${record.deploymentId}/${record.resourceId}`}>
                  <td className="font-mono">{record.name}</td>
                  <td>{record.type}</td>
                  <td className="font-mono">{record.value}</td>
                  <td>
                    <Link className="link" to={`/deployments/${record.deploymentId}`}>
                      {record.deploymentName}
                    </Link>
                  </td>
                  <td className="font-mono">{record.resourceId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The Private DNS tab (spec 11 6.15.9): per provider its zones, their links and records, read-only. */
export function DnsTab({ network }: NetworkTabProps) {
  const dns = useNetworkDns(network.data.id);
  if (dns.error) return <ErrorMessage error={dns.error} onRetry={() => void dns.refetch()} />;
  if (!dns.data) return <Loading />;
  const zones = dns.data.zones;
  const providers = [...new Set(zones.map((z) => z.provider))];
  return (
    <div className="space-y-4" data-testid="dns-tab">
      <p className="text-sm text-fg-muted">{d.heading}</p>
      {zones.length === 0 ? (
        <p className="help">{d.empty}</p>
      ) : (
        providers.map((provider) => (
          <section key={provider} aria-label={providerLabel(provider)} className="space-y-3">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold">
              <ProviderTile provider={provider} />
              {providerLabel(provider)}
            </h2>
            {zones
              .filter((z) => z.provider === provider)
              .map((zone) => (
                <ZoneSection key={zone.name} zone={zone} />
              ))}
          </section>
        ))
      )}
    </div>
  );
}
